import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useScenarios } from '../hooks/useScenarios';
import { ScenarioForm } from '../components/ScenarioForm';
import { Scenario, UpdateScenarioDto } from '../types';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { AssignLearnersModal } from '../../admin/components/AssignLearnersModal';
import { usePermissions } from '../../admin/hooks/usePermissions';
import { showToast, showError } from '../../../utils/alerts';

export const ScenarioEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canAssign } = usePermissions();
  const { getScenario, updateScenario, publishScenario } = useScenarios({ autoFetch: false });

  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getScenario(id)
      .then((data) => {
        if (data) {
          setScenario(data);
        } else {
          setError('Scenario not found.');
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleSaveDraft = async (data: UpdateScenarioDto) => {
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await updateScenario(id, data);
      if (res.success) {
        showToast('Scenario saved successfully!', 'success');
        navigate(`/scenarios/${id}`);
      } else {
        const errorMsg = res.error || 'Failed to update scenario';
        setError(errorMsg);
        showError('Update Failed', errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (data: UpdateScenarioDto) => {
    if (!id) return;
    setSubmitting(true);
    try {
      const updateRes = await updateScenario(id, data);
      if (!updateRes.success) {
        const errorMsg = updateRes.error || 'Failed to update scenario';
        setError(errorMsg);
        showError('Update Failed', errorMsg);
        return;
      }

      const assignedCount = (scenario as any)?.assignedCount || 0;
      if (assignedCount === 0 && scenario?.status !== 'PUBLISHED') {
        showToast('Scenario saved! Please assign learners to publish.', 'info');
        setIsAssignModalOpen(true);
        return;
      }

      const pubRes = await publishScenario(id);
      if (pubRes.success) {
        showToast('Scenario updated and published successfully!', 'success');
        navigate(`/scenarios/${id}`);
      } else {
        const errorMsg = pubRes.error || 'Failed to publish scenario';
        if (errorMsg.includes('assign') || errorMsg.includes('learner')) {
          showToast('Please assign learners to publish this scenario.', 'info');
          setIsAssignModalOpen(true);
        } else {
          setError(errorMsg);
          showError('Publish Failed', errorMsg);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignCompleted = async (count: number) => {
    if (!id) return;
    setIsAssignModalOpen(false);
    try {
      const pubRes = await publishScenario(id);
      if (pubRes.success) {
        showToast(`Assigned to ${count} learners & scenario published successfully!`, 'success');
      } else {
        showToast(`Assigned to ${count} learners. (Draft mode preserved)`, 'info');
      }
      navigate(`/scenarios/${id}`);
    } catch {
      navigate(`/scenarios/${id}`);
    }
  };

  const handleModalClose = () => {
    setIsAssignModalOpen(false);
    if (id) {
      navigate(`/scenarios/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading-state">
          <div className="dashboard-spinner" />
          <p>Loading scenario details…</p>
        </div>
      </div>
    );
  }

  if (!scenario || error) {
    return (
      <div className="admin-page">
        <div className="admin-alert admin-alert--error">{error || 'Scenario not found'}</div>
        <button type="button" onClick={() => navigate('/scenarios')} className="admin-btn admin-btn--secondary" style={{ width: 'fit-content' }}>
          ← Back to Scenarios
        </button>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Scenarios', to: '/scenarios' },
          { label: scenario.title, to: `/scenarios/${id}` },
          { label: 'Edit' },
        ]}
        icon="✏️"
        title={`Edit: ${scenario.title}`}
        badge={{
          label: scenario.status || 'DRAFT',
          bg: scenario.status === 'PUBLISHED' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(234, 179, 8, 0.2)',
          color: scenario.status === 'PUBLISHED' ? '#86EFAC' : '#FDE047',
          border: scenario.status === 'PUBLISHED' ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid rgba(250, 204, 21, 0.3)',
        }}
        description={
          <span>
            Category: <strong style={{ color: '#fff' }}>{scenario.category || 'General'}</strong> · Difficulty: <strong style={{ color: '#fff' }}>{scenario.difficulty || 'Medium'}</strong> · Passing Score: <strong style={{ color: '#fff' }}>{scenario.passScore ?? 70}%</strong>
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link
              to={`/scenarios/${id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              👁 View Dossier
            </Link>
          </div>
        }
      />

      <ScenarioForm
        initialValues={scenario}
        isSubmitting={submitting}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        onCancel={() => navigate(`/scenarios/${id}`)}
      />

      {/* Assign Learners Modal */}
      {canAssign && id && scenario && (
        <AssignLearnersModal
          simulationId={id}
          simulationTitle={scenario.title}
          tenantId={scenario.tenantId}
          isOpen={isAssignModalOpen}
          onClose={handleModalClose}
          onAssigned={handleAssignCompleted}
        />
      )}
    </div>
  );
};
